/** Einrichtungsassistent (Screen laden/Einrichtung): Laden → Preise & Klassen → Regale → SumUp → Personal → Startklar. Excel-Import ist V2. */
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select, Split } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { useLaden } from "./api";
import { useEinrichtung, useEinrichtungAbschliessen, useEinrichtungSchritt, type EinrichtungStand } from "./buchungen-api";
import { useRegaleMassen, useRegalklassen } from "./regale-api";
import { StammdatenFormular } from "./Einstellungen";
import { RegalklassenKarte } from "./EinstellungenPreise";
import { Personal } from "./Stammdaten";

type Schritt = EinrichtungStand["erledigt"][number] | "fertig";
const SCHRITTE: { key: Schritt; titel: string }[] = [
  { key: "laden", titel: "Laden" },
  { key: "preise", titel: "Preise & Klassen" },
  { key: "regale", titel: "Regale" },
  { key: "sumup", titel: "SumUp" },
  { key: "personal", titel: "Personal" },
  { key: "fertig", titel: "Startklar" },
];

function erledigtIn(d: EinrichtungStand, k: Schritt): boolean {
  return k === "fertig" ? !!d.abgeschlossenAt : d.erledigt.includes(k) || (k === "laden" ? d.stand.laden : k === "preise" ? d.stand.preise : k === "regale" ? d.stand.regale > 0 : k === "sumup" ? d.stand.sumup : d.stand.personal > 1);
}

export function Einrichtung() {
  const stand = useEinrichtung();
  return <Seitenzustand query={stand} lines={6}>{(d) => <Assistent d={d} />}</Seitenzustand>;
}

/** Der aktive Schritt wird einmal beim Öffnen bestimmt (erster offener Schritt) und springt danach nur per Klick oder „Weiter“. */
function Assistent({ d }: { d: EinrichtungStand }) {
  const laden = useLaden();
  const schrittSetzen = useEinrichtungSchritt();
  const abschliessen = useEinrichtungAbschliessen();
  const navigate = useNavigate();
  const [aktuell, setAktiv] = useState<Schritt>(() => SCHRITTE.find((s) => !erledigtIn(d, s.key))?.key ?? "fertig");
  const erledigt = (k: Schritt) => erledigtIn(d, k);
  const weiter = (k: EinrichtungStand["erledigt"][number]) => {
    schrittSetzen.mutate({ schritt: k });
    const i = SCHRITTE.findIndex((s) => s.key === k);
    setAktiv(SCHRITTE[i + 1]?.key ?? "fertig");
  };
  {
    {
      {
        return (
          <>
            <PageHeader kicker="Einrichtung" title="Laden einrichten" sub="Sechs Schritte, jederzeit unterbrechbar – alles lässt sich später in den Einstellungen ändern" actions={<Link to="/laden" className="rg-btn rg-btn--quiet">Später weitermachen</Link>} />
            <div className="sc-steps" style={{ marginBottom: "var(--space-4)" }}>
              {SCHRITTE.map((s, i) => (
                <button key={s.key} type="button" className={["sc-step", s.key === aktuell ? "is-on" : erledigt(s.key) ? "is-done" : ""].filter(Boolean).join(" ")} onClick={() => setAktiv(s.key)} style={{ border: 0, cursor: "pointer", font: "inherit" }}>
                  <span className="sc-step__n">{i + 1}</span>
                  {s.titel}
                </button>
              ))}
            </div>

            {aktuell === "laden" ? (
              <Split
                main={<Seitenzustand query={laden}>{(l) => <StammdatenFormular daten={l} onGespeichert={() => weiter("laden")} knopfText="Speichern und weiter" />}</Seitenzustand>}
                aside={<Card title="Gut zu wissen"><p className="sc-muted">Name, Adresse und Steuernummer erscheinen auf Mietverträgen, Abrechnungen und Rechnungen. Der USt-Modus bestimmt den Steuerhinweis im Vertrag.</p></Card>}
              />
            ) : null}

            {aktuell === "preise" ? (
              <Split
                main={
                  <>
                    <RegalklassenKarte kompakt />
                    <Notice tone="info">Provision, Kaution und Miet-Abo stellst du unter <Link to="/laden/einstellungen/preise">Einstellungen › Preise & Miete</Link> ein (Standard: 20 % Provision, Abo erlaubt, Frist 2 Wochen).</Notice>
                    <div className="app-form__actions">
                      <Button variant="secondary" icon="chevron-left" onClick={() => setAktiv("laden")}>Zurück</Button>
                      <Button variant="primary" iconEnd="chevron-right" disabled={!d.stand.preise} onClick={() => weiter("preise")}>Weiter zu den Regalen</Button>
                      {!d.stand.preise ? <Button variant="quiet" onClick={() => weiter("preise")}>Schritt überspringen</Button> : null}
                    </div>
                  </>
                }
                aside={<Card title="Beispiel"><p className="sc-muted">„Standard“ 39,00 € je Monat · „Premium“ (Schaufenster) 59,00 € je Monat · „Tisch“ 19,00 € je Woche. Jede Klasse kann Wochen- und/oder Monatspreis haben.</p></Card>}
              />
            ) : null}

            {aktuell === "regale" ? <RegaleAnlegen vorhanden={d.stand.regale} onWeiter={() => weiter("regale")} onZurueck={() => setAktiv("preise")} /> : null}

            {aktuell === "sumup" ? (
              <Split
                main={
                  <Card title="SumUp verbinden">
                    {d.stand.sumup ? <Notice tone="success" title="SumUp ist verbunden">Zahlungen werden an der Kasse automatisch erkannt.</Notice> : <Notice tone="info" title="Noch nicht verbunden">Die Verbindung läuft über <Link to="/laden/einstellungen/kasse">Einstellungen › Kasse & SumUp</Link>. Ohne Verbindung wartet die Kasse auf eine manuelle Zuordnung – der Schritt lässt sich auch später erledigen.</Notice>}
                    <div className="app-form__actions">
                      <Button variant="secondary" icon="chevron-left" onClick={() => setAktiv("regale")}>Zurück</Button>
                      <Link to="/laden/einstellungen/kasse" className="rg-btn rg-btn--secondary">Zu Kasse & SumUp</Link>
                      <Button variant="primary" iconEnd="chevron-right" onClick={() => weiter("sumup")}>{d.stand.sumup ? "Weiter" : "Später verbinden, weiter"}</Button>
                    </div>
                  </Card>
                }
                aside={<Card title="Was du brauchst"><p className="sc-muted">Das SumUp-Händlerkonto des Ladens (Login im Browser). Die Software liest nur Transaktionen – kein Zugriff auf Auszahlungen.</p></Card>}
              />
            ) : null}

            {aktuell === "personal" ? (
              <>
                <Personal eingebettet />
                <div className="app-form__actions" style={{ marginTop: "var(--space-4)" }}>
                  <Button variant="secondary" icon="chevron-left" onClick={() => setAktiv("sumup")}>Zurück</Button>
                  <Button variant="primary" iconEnd="chevron-right" onClick={() => weiter("personal")}>Weiter</Button>
                </div>
              </>
            ) : null}

            {aktuell === "fertig" ? (
              <Split
                main={
                  <Card title="Startklar?">
                    <div className="sc-col">
                      {SCHRITTE.filter((s) => s.key !== "fertig").map((s) => (
                        <div key={s.key} className="sc-row" style={{ justifyContent: "space-between" }}>
                          <span>{s.titel}</span>
                          {erledigt(s.key) ? <StatusBadge status="paid">Erledigt</StatusBadge> : <StatusBadge status="attention">Offen</StatusBadge>}
                        </div>
                      ))}
                    </div>
                    <p className="sc-muted" style={{ marginTop: "var(--space-4)" }}>{d.stand.regale} Regale · {d.stand.personal} Personen · SumUp {d.stand.sumup ? "verbunden" : "noch nicht verbunden"}</p>
                    <div className="app-form__actions" style={{ marginTop: "var(--space-4)" }}>
                      <Button variant="primary" icon="check" loading={abschliessen.isPending} onClick={() => abschliessen.mutate(undefined, { onSuccess: () => navigate("/laden") })}>Einrichtung abschließen</Button>
                      <Link to="/laden/buchungen/neu" className="rg-btn rg-btn--secondary">Erste Buchung anlegen</Link>
                    </div>
                  </Card>
                }
                aside={<Card title="Danach"><p className="sc-muted">Mieter anlegen, Regale buchen, Verträge senden. Artikel und Etiketten kommen mit dem Mieterportal (Etappe 4).</p></Card>}
              />
            ) : null}
          </>
        );
      }
    }
  }
}

function RegaleAnlegen({ vorhanden, onWeiter, onZurueck }: { vorhanden: number; onWeiter: () => void; onZurueck: () => void }) {
  const klassen = useRegalklassen();
  const massen = useRegaleMassen();
  const [form, setForm] = useState({ anzahl: "20", nummerAb: String(vorhanden + 1).padStart(3, "0"), regalklasseId: "", groesse: "" });
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<{ angelegt: string[]; uebersprungen: string[] } | null>(null);
  const n = Math.max(1, Math.min(500, parseInt(form.anzahl, 10) || 0));
  const ab = parseInt(form.nummerAb, 10) || 1;
  const vorschau = Array.from({ length: Math.min(n, 10) }, (_, i) => String(ab + i).padStart(3, "0"));

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!/^\d{3}$/.test(form.nummerAb)) return setFehler("Nummern ab: dreistellig, z. B. 001.");
    try {
      setErgebnis(await massen.mutateAsync({ anzahl: n, nummerAb: form.nummerAb, regalklasseId: form.regalklasseId || null, groesse: form.groesse.trim() }));
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Anlegen hat nicht geklappt.");
    }
  }

  return (
    <Split
      main={
        <form className="app-form" onSubmit={absenden} noValidate>
          <Card title="Regale anlegen">
            <p className="sc-muted">Alle Regale auf einmal anlegen. Größe, Lage und Klasse lassen sich später je Regal ändern.</p>
            {fehler ? <Notice tone="error">{fehler}</Notice> : null}
            {ergebnis ? <Notice tone="success" title={`${ergebnis.angelegt.length} Regale angelegt`}>{ergebnis.angelegt.length ? `${ergebnis.angelegt[0]} bis ${ergebnis.angelegt.at(-1)}` : ""}{ergebnis.uebersprungen.length ? ` · ${ergebnis.uebersprungen.length} Nummern gab es schon` : ""}</Notice> : null}
            <FormGrid>
              <TextField label="Anzahl Regale" value={form.anzahl} onChange={(e) => setForm({ ...form, anzahl: e.target.value })} inputMode="numeric" />
              <TextField label="Nummern ab" value={form.nummerAb} onChange={(e) => setForm({ ...form, nummerAb: e.target.value })} inputMode="numeric" hint="Dreistellig, wie auf den Etiketten" />
              <Select label="Regalklasse" value={form.regalklasseId} onChange={(e) => setForm({ ...form, regalklasseId: e.target.value })}>
                <option value="">Keine (später zuordnen)</option>
                {(klassen.data?.klassen ?? []).filter((k) => k.aktiv).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </Select>
              <TextField label="Standardgröße" value={form.groesse} onChange={(e) => setForm({ ...form, groesse: e.target.value })} placeholder="z. B. 4 Böden, 80 × 40 cm" />
            </FormGrid>
            <p className="sc-muted sc-small">Vorschau: {vorschau.join(" ")}{n > 10 ? ` … bis ${String(ab + n - 1).padStart(3, "0")}` : ""} · Etikett-Codes beginnen dann mit Mieternummer und Regal, z. B. 1234-{String(ab).padStart(3, "0")}-025.</p>
            <div className="app-form__actions">
              <Button variant="secondary" icon="chevron-left" onClick={onZurueck}>Zurück</Button>
              <Button type="submit" variant="primary" loading={massen.isPending}>{n} Regale anlegen</Button>
              <Button variant="quiet" onClick={onWeiter}>{vorhanden + (ergebnis?.angelegt.length ?? 0) > 0 ? "Weiter" : "Schritt überspringen"}</Button>
            </div>
          </Card>
        </form>
      }
      aside={<Card title="Gut zu wissen"><p className="sc-muted">{vorhanden ? `${vorhanden} Regale gibt es schon. ` : ""}Freie Regale erscheinen sofort im Belegungskalender. Mieter folgen im nächsten Schritt oder unter „Mieter“.</p></Card>}
    />
  );
}
